import { useMemo, useState } from "react";
import { Buffer } from "buffer";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  ClipboardCopy,
  FileCode2,
  RotateCcw,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";

import {
  bytesToHex,
  EncodingError,
  hexToBytes,
  type InputMode,
  textToBytes,
} from "@/lib/encoding";
import {
  addOpReturn,
  detectFormat,
  MAX_OP_RETURN_BYTES,
  PsbtError,
  type TxInputFormat,
} from "@/lib/psbt";

export function OpReturnTool() {
  const { toast } = useToast();

  const [mode, setMode] = useState<InputMode>("text");
  const [message, setMessage] = useState("");
  const [psbtInput, setPsbtInput] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [resultFormat, setResultFormat] = useState<TxInputFormat>("unknown");
  const [resultCopied, setResultCopied] = useState(false);

  const detectedFormat = useMemo(
    () => detectFormat(psbtInput),
    [psbtInput],
  );

  // Live byte computation + validation of the message field.
  const { bytes, byteError } = useMemo(() => {
    try {
      const b =
        mode === "text" ? textToBytes(message) : hexToBytes(message);
      return { bytes: b as Buffer, byteError: null as string | null };
    } catch (e) {
      const err = e instanceof EncodingError ? e.message : "Invalid input.";
      return { bytes: null as Buffer | null, byteError: err };
    }
  }, [message, mode]);

  const byteCount = bytes?.length ?? 0;
  const overLimit = byteCount > MAX_OP_RETURN_BYTES;
  const hexPreview = bytes ? bytesToHex(bytes) : "";

  const canGenerate =
    !!bytes &&
    byteCount > 0 &&
    !overLimit &&
    psbtInput.trim().length > 0 &&
    detectedFormat !== "unknown";

  function handleGenerate() {
    if (!bytes) return;
    try {
      const { output, format } = addOpReturn(psbtInput, bytes);
      setResult(output);
      setResultFormat(format);
      setResultCopied(false);
      toast({
        title: "OP_RETURN added",
        description:
          format === "rawhex"
            ? "Your new raw transaction is ready. Load it back into Sparrow."
            : "Your new PSBT is ready. Load it back into Sparrow.",
      });
    } catch (e) {
      const msg =
        e instanceof PsbtError
          ? e.message
          : "The PSBT could not be edited. Please check your input.";
      toast({
        title: "Error",
        description: msg,
        variant: "destructive",
      });
    }
  }

  async function copyResult() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setResultCopied(true);
      toast({
        title: "Copied",
        description:
          resultFormat === "rawhex"
            ? "New raw transaction is in your clipboard."
            : "New PSBT is in your clipboard.",
      });
      setTimeout(() => setResultCopied(false), 2500);
    } catch {
      toast({
        title: "Copy failed",
        description: "Please select the text manually and copy it.",
        variant: "destructive",
      });
    }
  }

  function reset() {
    setMessage("");
    setPsbtInput("");
    setResult(null);
    setResultFormat("unknown");
    setResultCopied(false);
  }

  return (
    <div className="space-y-6">
      {/* Step 1: the data */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <StepBadge n={1} />
            <CardTitle className="text-lg">Enter your message</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs
            value={mode}
            onValueChange={(v) => setMode(v as InputMode)}
            className="w-full"
          >
            <TabsList className="grid w-full max-w-xs grid-cols-2">
              <TabsTrigger value="text">Text</TabsTrigger>
              <TabsTrigger value="hex">Hex</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-2">
            <Label htmlFor="message">
              {mode === "text"
                ? "Message (plain text)"
                : "Data as hex (e.g. 48656c6c6f)"}
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                mode === "text"
                  ? "e.g. Hello blockchain!"
                  : "e.g. 48656c6c6f20576f726c6421"
              }
              className="min-h-24 font-mono text-sm"
              aria-invalid={overLimit || !!byteError}
            />
          </div>

          {/* byte meter */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Size</span>
              <span
                className={cn(
                  "font-mono font-medium",
                  overLimit ? "text-destructive" : "text-foreground",
                )}
              >
                {byteCount} / {MAX_OP_RETURN_BYTES} bytes
              </span>
            </div>
            <Progress
              value={Math.min((byteCount / MAX_OP_RETURN_BYTES) * 100, 100)}
              className={cn(overLimit && "[&>div]:bg-destructive")}
            />
          </div>

          {byteError && (
            <p className="text-sm text-destructive">{byteError}</p>
          )}
          {overLimit && (
            <p className="text-sm text-destructive">
              Too large! This tool allows up to {MAX_OP_RETURN_BYTES} bytes.
            </p>
          )}

          {mode === "text" && bytes && byteCount > 0 && !overLimit && (
            <div className="rounded-lg bg-muted/60 p-3">
              <p className="text-xs text-muted-foreground mb-1">
                Automatically encoded as hex:
              </p>
              <code className="text-xs font-mono break-all">{hexPreview}</code>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: PSBT / raw tx in */}
      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <StepBadge n={2} />
            <CardTitle className="text-lg">
              Paste your transaction from Sparrow
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert className="bg-muted/40">
            <FileCode2 className="h-4 w-4" />
            <AlertTitle>Two formats are accepted</AlertTitle>
            <AlertDescription className="text-sm leading-relaxed">
              Build your transaction in Sparrow as usual, but{" "}
              <strong>do not sign it yet</strong>. Then paste either:
              <ul className="mt-2 ml-4 list-disc space-y-1">
                <li>
                  a <strong>PSBT (Base64)</strong> via{" "}
                  <span className="font-medium">
                    File → Save PSBT → As Base64
                  </span>
                  , or
                </li>
                <li>
                  a <strong>raw transaction in hex</strong> (what Sparrow shows
                  on the transaction's "Hex" tab).
                </li>
              </ul>
              The format is detected automatically.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="psbt">PSBT (Base64) or raw transaction (hex)</Label>
              {psbtInput.trim().length > 0 && (
                <Badge
                  variant={
                    detectedFormat === "unknown" ? "destructive" : "secondary"
                  }
                  className="font-mono text-[10px]"
                >
                  {detectedFormat === "psbt"
                    ? "PSBT detected"
                    : detectedFormat === "rawhex"
                      ? "Raw hex detected"
                      : "Unknown format"}
                </Badge>
              )}
            </div>
            <Textarea
              id="psbt"
              value={psbtInput}
              onChange={(e) => setPsbtInput(e.target.value)}
              placeholder="cHNidP8BAHQC...  or  0200000001..."
              className="min-h-28 font-mono text-xs"
            />
            {psbtInput.trim().length > 0 && detectedFormat === "unknown" && (
              <p className="text-sm text-destructive">
                This doesn't look like a Base64 PSBT or a raw hex transaction.
                Please check what you pasted.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Generate */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="lg"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="gap-2"
        >
          Add OP_RETURN
          <ArrowRight className="h-4 w-4" />
        </Button>
        <Button
          size="lg"
          variant="ghost"
          onClick={reset}
          className="gap-2 text-muted-foreground"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>

      {/* Step 3: result */}
      {result && (
        <Card className="border-primary/40 shadow-md ring-1 ring-primary/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <StepBadge n={3} />
              <CardTitle className="text-lg">
                {resultFormat === "rawhex"
                  ? "Finished transaction – load it back into Sparrow"
                  : "Finished PSBT – load it back into Sparrow"}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Check className="h-4 w-4" />
              <AlertTitle>Done!</AlertTitle>
              <AlertDescription className="text-sm leading-relaxed">
                Copy the text below. In Sparrow:{" "}
                <span className="font-medium">
                  File → Open Transaction → From Text
                </span>
                {resultFormat === "rawhex" && (
                  <>
                    {" "}
                    (paste the hex). Any previous signatures were removed, so
                    this is a fresh unsigned transaction.
                  </>
                )}
                . Your OP_RETURN will appear as a new output with amount{" "}
                <strong>0</strong>. Then sign and send as usual.
              </AlertDescription>
            </Alert>

            <div className="relative">
              <Textarea
                readOnly
                value={result}
                className="min-h-32 font-mono text-xs pr-12"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                size="icon"
                variant="secondary"
                onClick={copyResult}
                className="absolute right-2 top-2"
                aria-label="Copy"
              >
                {resultCopied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <ClipboardCopy className="h-4 w-4" />
                )}
              </Button>
            </div>

            <Button onClick={copyResult} className="gap-2">
              {resultCopied ? (
                <>
                  <Check className="h-4 w-4" /> Copied
                </>
              ) : (
                <>
                  <ClipboardCopy className="h-4 w-4" />{" "}
                  {resultFormat === "rawhex"
                    ? "Copy new transaction"
                    : "Copy new PSBT"}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Safety note */}
      <Alert variant="destructive" className="bg-destructive/5">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Important</AlertTitle>
        <AlertDescription className="text-sm leading-relaxed">
          OP_RETURN data is stored <strong>publicly and permanently</strong> on
          the Bitcoin blockchain. Add the OP_RETURN <strong>before</strong>{" "}
          signing in Sparrow – editing afterwards invalidates any existing
          signatures. Everything runs locally in your browser; nothing is
          uploaded.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function StepBadge({ n }: { n: number }) {
  return (
    <Badge
      variant="outline"
      className="h-7 w-7 justify-center rounded-full border-primary/30 bg-primary/10 p-0 text-sm font-bold text-primary"
    >
      {n}
    </Badge>
  );
}
